import useFormDetailsPage from '@/hooks/use-form-details-page';
import { CheckIcon, ChevronDownIcon, PlusIcon } from 'lucide-react';

import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';

import FormConfigureStages from '@/features/form-responses/components/common/form-configure-stages';

import { Tooltip } from '@/shared/components/tooltip';

import { cn } from '@/lib/utils';

import { CUSTOM_STAGES } from '../constants/stage';
import SimpleDropdown from './SimpleDropdown';

const CONFIGURE_STAGES_VALUE = '!!@configure-stages#459*3';
function StageSelectorDropdown({
	defaultStage,
	onStageSelect,
	dropdownWidth = 280,
	useIcon = true,
	buttonClassName,
	triggerButtonProps = {},
	large,
	modal = false,
	getButtonText,
	filterOutStages = [],
	allowEmptyStage = false,
	placeholder = 'Change stage',
	showConfigureButton = false,
	showTriggerAsIcon = false,
	iconTriggerTooltipText,
	doNotSelectAnyStage = false,
	preventDeletionOfStages = false,
	fromMessagesPage = false,
	wrapperClassName,
}: {
	doNotSelectAnyStage?: boolean;
	allowEmptyStage?: boolean;
	filterOutStages?: string[];
	dropdownWidth?: number;
	defaultStage?: string;
	useIcon?: boolean;
	buttonClassName?: string;
	onStageSelect: (stage: string) => void;
	triggerButtonProps?: React.ComponentProps<typeof Button>;
	large?: boolean;
	modal?: boolean;
	getButtonText?: (stage: string) => string;
	placeholder?: string;
	showConfigureButton?: boolean;
	showTriggerAsIcon?: boolean;
	iconTriggerTooltipText?: string;
	preventDeletionOfStages?: boolean;
	fromMessagesPage?: boolean;
	wrapperClassName?: string;
}) {
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const [selectedStage, setSelectedStage] = useState(defaultStage);
	const [showConfigureStageModal, setShowConfigureStageModal] = useState(false);

	const { updatedFormDetails: formDetails } = useFormDetailsPage();

	const netStagesToDisplay = useMemo(() => {
		return (formDetails?.stages ?? []).filter((stage) => !filterOutStages.includes(stage.label));
	}, [formDetails?.stages, filterOutStages]);

	useEffect(() => {
		const newStage = allowEmptyStage
			? defaultStage
			: defaultStage || netStagesToDisplay?.[0]?.label;

		if (!doNotSelectAnyStage) setSelectedStage(newStage);
	}, [defaultStage]);

	const iconTrigger = (
		<Button
			variant="ghost"
			size="icon"
			className={cn('bg-transparent hover:bg-transparent', buttonClassName)}
			{...triggerButtonProps}
		>
			<ChevronDownIcon
				className={cn(
					'size-4 text-secondary/70 transition-transform hover:text-secondary',
					dropdownOpen && 'rotate-180'
				)}
			/>
		</Button>
	);

	return (
		<>
			<SimpleDropdown
				open={dropdownOpen}
				onOpenChange={setDropdownOpen}
				truncateLongOptions
				options={[
					...netStagesToDisplay
						?.map((stage) => stage.label)
						?.map((stage) => ({
							label: stage,
							value: stage,
							onClick: () => {
								setDropdownOpen(false);
								// No optimistic select: the picker shows the new stage once the caller's move
								// succeeds and `defaultStage` changes, so a refused move needs no revert.
								onStageSelect?.(stage);
							},
							icon: useIcon ? (
								selectedStage === stage ? (
									<CheckIcon className="size-4" />
								) : (
									<div className="size-4" />
								)
							) : undefined,
						})),
					...(showConfigureButton
						? [
								{
									label: 'Add another stage',
									value: CONFIGURE_STAGES_VALUE,
									onClick: () => {
										setDropdownOpen(false);
										setShowConfigureStageModal(true);
									},
									icon: <PlusIcon className="size-4" />,
								},
							]
						: []),
				]}
				contentStyle={{
					width: dropdownWidth ? `${dropdownWidth}px` : '200px',
				}}
				modal={modal}
			>
				<div className={cn('w-full', wrapperClassName)}>
					<Tooltip text={iconTriggerTooltipText ?? ''} delayDuration={300}>
						{showTriggerAsIcon ? (
							iconTrigger
						) : (
							<Button
								variant={selectedStage === CUSTOM_STAGES.NEW ? 'outline' : 'secondary'}
								size={large ? 'lg' : 'sm'}
								className={cn(
									'gap-2 rounded-full border px-4 transition-all duration-300',
									selectedStage === CUSTOM_STAGES.ARCHIVED
										? 'border-slate-700 bg-slate-700 text-white hover:bg-slate-700/90 focus:bg-slate-700/90 active:bg-slate-700/90'
										: selectedStage !== CUSTOM_STAGES.NEW
											? 'border-primary-blue bg-primary-blue text-white hover:bg-primary-blue/90 focus:bg-primary-blue/90 active:bg-primary-blue/90'
											: '',
									buttonClassName
								)}
								{...triggerButtonProps}
							>
								<span
									className={cn(
										'inline-block truncate',
										getButtonText ? '' : 'capitalize',
										selectedStage ? '' : 'text-gray-400'
									)}
								>
									{selectedStage
										? getButtonText
											? getButtonText(selectedStage)
											: selectedStage
										: placeholder}
								</span>

								<ChevronDownIcon
									className={cn('size-4 transition-transform', dropdownOpen && 'rotate-180')}
								/>
							</Button>
						)}
					</Tooltip>
				</div>
			</SimpleDropdown>

			<FormConfigureStages
				withoutTrigger
				open={showConfigureStageModal}
				onOpenChange={setShowConfigureStageModal}
				preventDeletion={preventDeletionOfStages}
				fromMessagesPage={fromMessagesPage}
			/>
		</>
	);
}

export default StageSelectorDropdown;
